import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
} from '@duck/ui'
import { useTar } from '@/modules'
import { StandardLoader } from '../StandardLoader'
import { toast } from 'sonner'

interface DeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDelete: () => Promise<void>
  title?: string
  description?: string
  deleteButtonText?: string
  warning?: string
}

export const DeleteDialog: React.FC<DeleteDialogProps> = ({
  open,
  onOpenChange,
  onDelete,
  title,
  description,
  deleteButtonText,
  warning,
}) => {
  const { t } = useTar()
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    try {
      setIsDeleting(true)
      await onDelete()
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to delete:', error)
      toast.error(t('errors.deleteFailed'))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCancel = () => {
    if (!isDeleting) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent width="xs" noMinHeight>
        <DialogHeader>
          <DialogTitle>
            {title || t('common.delete')}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-3">
          {warning && (
            <p className="text-sm font-bold text-foreground">
              {warning}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            {description || t('errors.deleteDescription')}
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="default"
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
          >
            {isDeleting ? (
              <>
                <StandardLoader size="sm" inline />
                <span className="ml-2">{t('common.deleting')}</span>
              </>
            ) : (
              deleteButtonText || t('common.delete')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
